'use client'

import { Button, Cell, InputSelect, InputText, Spacer } from '@vinted/web-ui'
import { camelCase } from 'lodash'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-ui'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  BusinessAddressModel,
  validateBusinessUboAddress,
} from '@marketplace-web/domain/business-accounts-data'
import {
  CountryModel,
  getCountries,
  transformCountryDtos,
} from '@marketplace-web/domain/country-data'

import { AddressField } from '../../types'
import useRequiredAddressField from './useRequiredAddressField'

type Props = {
  address: BusinessAddressModel | null
  onSubmit: (values: BusinessAddressModel) => void
  onCancel: () => void
}

const Address = ({ address, onSubmit, onCancel }: Props) => {
  const translate = useTranslate('kyc_form.business.ubo.address.modal')

  const defaultValues = address || undefined

  const {
    register,
    getValues,
    setError,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BusinessAddressModel>({ defaultValues })

  const requiredField = useRequiredAddressField()
  const validationMessage = useFormValidationMessage(errors)

  const [loadState, setLoadState] = useState<UiState>(UiState.Idle)
  const [countries, setCountries] = useState<Array<CountryModel>>([])

  const canSubmit = loadState === UiState.Success

  useEffect(() => {
    async function fetchCountries() {
      setLoadState(UiState.Pending)

      const response = await getCountries()

      if ('errors' in response) return

      const transformedCountries = transformCountryDtos(response.countries)

      // eslint-disable-next-line @typescript-eslint/no-extra-non-null-assertion
      setValue('countryId', transformedCountries[0]!?.id)

      setCountries(transformedCountries)
      setLoadState(UiState.Success)
    }

    fetchCountries()
  }, [setValue])

  async function handleContinueClick() {
    const values = getValues()

    const formattedValue = {
      line1: values.line1,
      line2: values.line2,
      city: values.city,
      postal_code: values.postalCode,
      country_id: values.countryId,
    }

    const response = await validateBusinessUboAddress(formattedValue)

    if ('errors' in response) {
      response.errors.forEach(error => {
        const field = camelCase(error.field) as AddressField

        setError(field, { type: 'manual', message: error.value })
      })

      return
    }

    onSubmit(values)
  }

  function renderCountries() {
    return countries.map(country => ({ value: country.id, label: country.title }))
  }

  function handleSubmitClick(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (isSubmitting) {
      event.preventDefault()

      return
    }

    handleSubmit(handleContinueClick)()
  }

  if (loadState !== UiState.Success) {
    return <ContentLoader styling={ContentLoader.Styling.Wide} />
  }

  return (
    <>
      <Cell>
        <SeparatedList separator={<Spacer size={Spacer.Size.XLarge} />}>
          <InputSelect
            {...register(AddressField.CountryId, {
              required: requiredField(AddressField.CountryId),
            })}
            options={renderCountries()}
            styling={InputSelect.Styling.Tight}
            title={translate('fields.country_id.title')}
            validation={renderValidation(validationMessage(AddressField.CountryId))}
          />
          <InputText
            {...register(AddressField.City, {
              required: requiredField(AddressField.City),
            })}
            styling={InputText.Styling.Tight}
            title={translate('fields.city.title')}
            placeholder={translate('fields.city.placeholder')}
            validation={renderValidation(validationMessage(AddressField.City))}
          />
          <InputText
            {...register(AddressField.Line1, {
              required: requiredField(AddressField.Line1),
            })}
            styling={InputText.Styling.Tight}
            title={translate('fields.line_1.title')}
            placeholder={translate('fields.line_1.placeholder')}
            validation={renderValidation(validationMessage(AddressField.Line1))}
          />
          <InputText
            {...register(AddressField.Line2)}
            styling={InputText.Styling.Tight}
            title={translate('fields.line_2.title')}
            placeholder={translate('fields.line_2.placeholder')}
            validation={renderValidation(validationMessage(AddressField.Line2))}
          />
          <InputText
            {...register(AddressField.PostalCode, {
              required: requiredField(AddressField.PostalCode),
            })}
            styling={InputText.Styling.Tight}
            title={translate('fields.postal_code.title')}
            placeholder={translate('fields.postal_code.placeholder')}
            validation={renderValidation(validationMessage(AddressField.PostalCode))}
          />
        </SeparatedList>
      </Cell>
      <Cell>
        <Button
          styling={Button.Styling.Filled}
          disabled={!canSubmit || isSubmitting}
          isLoading={isSubmitting}
          text={translate('buttons.continue')}
          theme="primary"
          onClick={handleSubmitClick}
        />
        <Spacer size={Spacer.Size.Medium} />
        <Button
          styling={Button.Styling.Flat}
          disabled={isSubmitting}
          text={translate('buttons.cancel')}
          onClick={onCancel}
        />
      </Cell>
    </>
  )
}

export default Address
